import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Save, Plus, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMEZONES = [
  "America/Chicago", "America/New_York", "America/Los_Angeles", "America/Denver",
  "America/Phoenix", "America/Anchorage", "Pacific/Honolulu",
  "Europe/London", "Europe/Berlin", "Europe/Paris", "Africa/Lagos",
  "Asia/Jerusalem", "Asia/Dubai", "Asia/Kolkata", "Asia/Tokyo",
  "Australia/Sydney", "UTC",
];

const PROTECTED_TAGS = ["truebeliever", "truebelieverfam"];

function PlatformTimes({ label, value, onChange, testid }) {
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950/40 p-3">
      <p className="mb-2 text-xs font-medium text-zinc-300">{label}</p>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col text-xs text-zinc-400">
          Morning
          <input
            type="time"
            value={value.morning}
            onChange={(e) => onChange({ ...value, morning: e.target.value })}
            data-testid={`${testid}-morning`}
            className="mt-1 rounded bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 ring-1 ring-zinc-800 focus:outline-none focus:ring-amber-400/40"
          />
        </label>
        <label className="flex flex-col text-xs text-zinc-400">
          Evening
          <input
            type="time"
            value={value.evening}
            onChange={(e) => onChange({ ...value, evening: e.target.value })}
            data-testid={`${testid}-evening`}
            className="mt-1 rounded bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 ring-1 ring-zinc-800 focus:outline-none focus:ring-amber-400/40"
          />
        </label>
      </div>
    </div>
  );
}

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const [tagDraft, setTagDraft] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/settings`);
        setSettings(r.data);
      } catch (e) {
        setError(e?.message || "Failed to load settings");
      }
    })();
  }, []);

  if (!settings) {
    return (
      <div className="flex items-center gap-2 text-sm text-zinc-400" data-testid="settings-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading settings…
      </div>
    );
  }

  const update = (patch) => setSettings({ ...settings, ...patch });
  const updateTimes = (platform, value) =>
    update({ posting_times: { ...settings.posting_times, [platform]: value } });

  const addTag = () => {
    const t = tagDraft.trim().toLowerCase().replace(/^#+/, "");
    if (!t) return;
    if (settings.mandatory_hashtags.includes(t)) {
      setTagDraft("");
      return;
    }
    update({ mandatory_hashtags: [...settings.mandatory_hashtags, t] });
    setTagDraft("");
  };

  const removeTag = (t) => {
    if (PROTECTED_TAGS.includes(t)) return;
    update({ mandatory_hashtags: settings.mandatory_hashtags.filter((x) => x !== t) });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const r = await axios.put(`${API}/settings`, settings);
      setSettings(r.data);
      setSavedAt(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="settings-page">
      <header className="mb-8">
        <p className="mb-1 text-xs uppercase tracking-[0.2em] text-zinc-500">Settings</p>
        <h1 className="text-3xl font-semibold tracking-tight">Posting preferences</h1>
        <p className="mt-2 text-sm text-zinc-400">
          Defaults are pre-filled. The scheduler reads these in Phase 3.
        </p>
      </header>

      <div className="space-y-6">
        {/* Timezone */}
        <section className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5">
          <h2 className="mb-3 text-sm font-medium">Timezone</h2>
          <select
            value={settings.timezone}
            onChange={(e) => update({ timezone: e.target.value })}
            data-testid="settings-timezone"
            className="w-full rounded bg-zinc-950 px-3 py-2 text-sm text-zinc-100 ring-1 ring-zinc-800 focus:outline-none focus:ring-amber-400/40"
          >
            {TIMEZONES.map((tz) => <option key={tz}>{tz}</option>)}
          </select>
          <p className="mt-1 text-xs text-zinc-500">All posting times below are interpreted in this zone.</p>
        </section>

        {/* Mandatory hashtags */}
        <section className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5">
          <h2 className="mb-3 text-sm font-medium">Mandatory hashtags</h2>
          <p className="mb-3 text-xs text-zinc-500">
            Added to every generated caption. <span className="text-amber-300">#truebeliever</span> and{" "}
            <span className="text-amber-300">#truebelieverfam</span> are locked.
          </p>
          <div className="mb-3 flex flex-wrap gap-2" data-testid="settings-hashtags">
            {settings.mandatory_hashtags.map((t) => {
              const locked = PROTECTED_TAGS.includes(t);
              return (
                <span
                  key={t}
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ring-1 ${
                    locked ? "bg-amber-400/15 text-amber-300 ring-amber-400/30" : "bg-zinc-800 text-zinc-200 ring-zinc-700"
                  }`}
                >
                  #{t}
                  {!locked && (
                    <button onClick={() => removeTag(t)} className="opacity-60 hover:opacity-100" aria-label={`Remove ${t}`}>
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
          <div className="flex gap-2">
            <input
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
              placeholder="add tag (no #)"
              data-testid="settings-hashtag-input"
              className="flex-1 rounded bg-zinc-950 px-3 py-2 text-sm text-zinc-100 ring-1 ring-zinc-800 focus:outline-none focus:ring-amber-400/40"
            />
            <button
              onClick={addTag}
              data-testid="settings-add-hashtag"
              className="inline-flex items-center gap-1.5 rounded bg-zinc-800 px-3 py-2 text-sm text-zinc-100 ring-1 ring-zinc-700 hover:bg-zinc-700"
            >
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>
        </section>

        {/* Posting times */}
        <section className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5">
          <h2 className="mb-1 text-sm font-medium">Posting times per platform</h2>
          <p className="mb-4 text-xs text-zinc-500">Two slots per day per platform (morning + evening). 24h format.</p>
          <div className="grid gap-3 md:grid-cols-2">
            {["youtube", "facebook", "instagram", "tiktok"].map((p) => (
              <PlatformTimes
                key={p}
                label={p === "tiktok" ? "TikTok" : p.charAt(0).toUpperCase() + p.slice(1)}
                value={settings.posting_times[p] || { morning: "09:00", evening: "20:00" }}
                onChange={(v) => updateTimes(p, v)}
                testid={`times-${p}`}
              />
            ))}
          </div>
        </section>

        {/* Notification email */}
        <section className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5">
          <h2 className="mb-3 text-sm font-medium">Failure notification email</h2>
          <input
            type="email"
            value={settings.notification_email}
            onChange={(e) => update({ notification_email: e.target.value })}
            data-testid="settings-email"
            className="w-full rounded bg-zinc-950 px-3 py-2 text-sm text-zinc-100 ring-1 ring-zinc-800 focus:outline-none focus:ring-amber-400/40"
          />
          <p className="mt-1 text-xs text-zinc-500">Phase 4 will send post-failure + token-expiry alerts here.</p>
        </section>

        {/* Save */}
        <div className="flex items-center justify-between">
          <div className="text-xs text-zinc-500">
            {savedAt && <span data-testid="settings-saved-at">Saved at {savedAt.toLocaleTimeString()}</span>}
            {error && <span className="text-red-400" data-testid="settings-error">{error}</span>}
          </div>
          <button
            onClick={save}
            disabled={saving}
            data-testid="settings-save"
            className="inline-flex items-center gap-2 rounded-md bg-amber-400 px-4 py-2 text-sm font-medium text-zinc-900 hover:bg-amber-300 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save settings
          </button>
        </div>
      </div>
    </div>
  );
}
